import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderBar from 'components/ui/HeaderBar';
import RoleBasedSidebar from 'components/ui/RoleBasedSidebar';
import BreadcrumbNavigation from 'components/ui/BreadcrumbNavigation';
import StatusIndicatorBadge from 'components/ui/StatusIndicatorBadge';
import Icon from 'components/AppIcon';
import { useAuth } from 'context/AuthContext';
import { useEmployerApplications } from 'hooks/useEmployerApplications';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const EmployerApplicationManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_date');
  const {
    applications,
    loading,
    error,
    refresh
  } = useEmployerApplications();

  useEffect(() => {
    if (user && (!user.eclabUser || user.role !== 3)) {
      navigate('/login-screen');
    }
  }, [user, navigate]);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleSelectWorkers = (application) => {
    navigate('/employer-biodata-selection', {
      state: {
        vdrApplication: application,
        fromApplications: true
      }
    });
  };

  const handleViewDetails = (application) => {
    if (application?.source === 'special_quota') {
      navigate(`/employer-special-quota/${application.vdrId}`);
      return;
    }
    navigate(`/employer-vdr-applications/${application.vdrId}`);
  };

  const handleCreateSpecialQuota = () => {
    navigate('/employer-special-quota/new');
  };

  const filteredApplications = useMemo(() => {
    return applications
      .filter(application => {
        const matchesSearch = searchTerm
          ? [
              application.companyName,
              application.vdrId,
              application.applicationId,
              application.nationality
            ].some(field => field?.toLowerCase().includes(searchTerm.toLowerCase()))
          : true;

        const matchesStatus = (() => {
          if (statusFilter === 'all') return true;
          if (statusFilter === 'selectable') return application.isSelectable;
          if (statusFilter === 'completed') return !application.isSelectable;
          return true;
        })();

        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        if (sortBy === 'created_date') {
          return new Date(b.createdDate || b.created_date || 0) - new Date(a.createdDate || a.created_date || 0);
        }
        if (sortBy === 'fcl_remaining') {
          const aAvailable = a.slots?.available ?? a.slots?.remaining ?? a.slots?.localRemaining ?? 0;
          const bAvailable = b.slots?.available ?? b.slots?.remaining ?? b.slots?.localRemaining ?? 0;
          return bAvailable - aAvailable;
        }
        if (sortBy === 'company_name') {
          return (a.companyName || '').localeCompare(b.companyName || '');
        }
        return 0;
      });
  }, [applications, searchTerm, statusFilter, sortBy]);
  

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <HeaderBar
          userRole="employer"
          onSidebarToggle={handleSidebarToggle}
          userName={user?.fullName || 'Employer'}
        />

        <RoleBasedSidebar
          userRole="employer"
          isCollapsed={sidebarCollapsed}
          onToggle={handleSidebarToggle}
        />

        <main className={`transition-all duration-300 ease-smooth pt-16 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        }`}>
          <div className="p-6">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-text-secondary">Loading your job applications...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar
        userRole="employer"
        onSidebarToggle={handleSidebarToggle}
        userName={user?.fullName || 'Employer'}
      />

      <RoleBasedSidebar
        userRole="employer"
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      <main className={`transition-all duration-300 ease-smooth pt-16 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="p-6">
          <div className="max-w-7xl mx-auto">
            <BreadcrumbNavigation userRole="employer" />

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">Job Applications</h1>
                <p className="text-text-secondary">
                  Manage your E-CLAB VDR applications, quotas, and shortlist progress
                </p>
              </div>

              <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                <button
                  onClick={handleCreateSpecialQuota}
                  className="flex items-center space-x-2 bg-secondary-100 text-text-primary px-4 py-2 rounded-lg hover:bg-secondary-200 transition-colors duration-200"
                >
                  <Icon name="Plus" size={20} />
                  <span>New Special Quota</span>
                </button>
                <button
                  onClick={refresh}
                  className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors duration-200"
                >
                  <Icon name="RefreshCw" size={20} />
                  <span>Refresh</span>
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg">
                <div className="flex items-center">
                  <Icon name="AlertCircle" size={20} className="text-error mr-3" />
                  <span className="text-error">{error}</span>
                </div>
              </div>
            )}

            <div className="bg-white border border-secondary-200 rounded-lg p-4 mb-6">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div className="w-full lg:w-1/3">
                  <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
                    Search
                  </label>
                  <div className="relative">
                    <Icon name="Search" size={16} className="absolute left-3 top-3 text-secondary-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by VDR ID, company, or nationality"
                      className="w-full pl-9 pr-3 py-2 border border-secondary-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div className="w-full lg:w-1/4">
                  <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary-100"
                  >
                    <option value="all">All statuses</option>
                    <option value="selectable">Ready for selection</option>
                    <option value="completed">Fully processed</option>
                  </select>
                </div>

                <div className="w-full lg:w-1/4">
                  <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
                    Sort By
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary-100"
                  >
                    <option value="created_date">Latest created</option>
                    <option value="fcl_remaining">Remaining slots</option>
                    <option value="company_name">Company name</option>
                  </select>
                </div>
              </div>

              
            </div>

            {filteredApplications.length === 0 ? (
              <div className="text-center py-12 border border-secondary-200 rounded-lg bg-white">
                <Icon name="FileX" size={48} className="text-secondary-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-text-primary mb-2">No VDR applications found</h3>
                <p className="text-text-secondary">Adjust your filters or check back later for new applications.</p>
              </div>
            ) : (
              <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-secondary-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">VDR Details</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Nationality</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">VDR Type / Biodata Type</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold text-text-primary">FCL Progress</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold text-text-primary">Shortlist</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold text-text-primary">Status</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold text-text-primary">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredApplications.map((application) => {
                        const activeShortlists = application.localStats?.shortlist?.active ?? 0;
                        const shortlistedCount = application.localStats?.workers?.selected ?? 0;
                        const approvedCount = application.localStats?.workers?.approved ?? 0;
                        const rejectedCount = application.localStats?.workers?.rejected ?? 0;
                        const slots = application.slots || {};
                        const totalSlots = slots.approved ?? slots.requested ?? 0;
                        const submittedSlots =
                          slots.used ??
                          Math.max(
                            totalSlots -
                              (slots.remaining ?? slots.localRemaining ?? totalSlots),
                            0
                          );
                        const remainingSlots =
                          slots.remaining ??
                          Math.max(totalSlots - submittedSlots, 0);
                        const availableSlots =
                          slots.available ?? slots.localRemaining ?? remainingSlots;

                        return (
                          <tr key={application.vdrId} className="border-b border-secondary-100 hover:bg-secondary-25 transition-colors">
                            <td className="px-6 py-4 align-top">
                              <div className="space-y-1">
                                <div className="font-semibold text-text-primary text-base">
                                  {application.vdrId}
                                </div>
                                <div className="text-sm text-text-secondary">
                                  {application.companyName}
                                </div>
                                <div className="text-xs text-text-secondary">
                                  Application ID: {application.applicationId}
                                </div>
                                <div className="text-xs text-text-secondary">
                                  Quota Expiry: {formatDate(application.validityStartDate)} – {formatDate(application.validityEndDate)}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 align-top">
                              <div className="flex items-center space-x-2">
                                <Icon name="Flag" size={16} className="text-text-secondary" />
                                <span className="text-text-primary font-medium">
                                  {application.nationality}
                                </span>
                              </div>
                              <div className="text-xs text-text-secondary mt-1">
                                Payment: {application.paymentStatus || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4 align-top">
                              <div className="space-y-2">
                                {application.vdrType && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-primary-50 text-primary-700">
                                    {application.vdrType}
                                  </span>
                                )}
                                {application.source === 'special_quota' && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                                    Special Quota
                                  </span>
                                )}
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  application.biodataTypeLabel === 'CLAB'
                                    ? 'bg-primary-100 text-primary-700'
                                    : application.biodataTypeLabel === 'OWN'
                                      ? 'bg-secondary-100 text-secondary-700'
                                      : 'bg-secondary-50 text-secondary-500'
                                }`}>
                                  {application.biodataTypeLabel}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 align-top">
                              <div className="space-y-2">
                                <div className="text-sm font-semibold text-text-primary text-center">
                                  {submittedSlots} / {totalSlots}
                                </div>
                                <div className="h-2 bg-secondary-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary rounded-full"
                                    style={{
                                      width: `${Math.min(100, ((submittedSlots / (totalSlots || 1)) * 100))}%`
                                    }}
                                  />
                                </div>
                                <div className="text-xs text-text-secondary text-center">
                                  {Math.max(availableSlots, 0)} slots available
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 align-top">
                              <div className="space-y-1 text-center text-xs text-text-secondary">
                                <div>
                                  <span className="font-semibold text-text-primary">{activeShortlists}</span> active holds
                                </div>
                                <div>{shortlistedCount} submitted</div>
                                <div className="text-success">{approvedCount} approved</div>
                                {rejectedCount > 0 && (
                                  <div className="text-error">{rejectedCount} rejected</div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 align-top text-center">
                              <StatusIndicatorBadge
                                status={application.isSelectable ? 'approved' : (application.status || 'default')}
                                size="sm"
                              />
                              <div className="text-xs text-text-secondary mt-1">
                                {application.isSelectable
                                  ? 'Ready for selection'
                                  : application.source === 'special_quota'
                                    ? (application.status === 'pending_quota_review'
                                      ? 'Pending admin review'
                                      : application.status === 'selection_submitted'
                                        ? 'Selection submitted'
                                        : 'Processing')
                                    : 'Processing'}
                              </div>
                            </td>
                            <td className="px-6 py-4 align-top">
                              <div className="flex flex-col items-center space-y-2">
                                <button
                                  onClick={() => handleViewDetails(application)}
                                  className="flex items-center space-x-1 text-text-secondary hover:text-primary transition-colors"
                                >
                                  <Icon name="Eye" size={16} />
                                  <span>View details</span>
                                </button>
                                {application.isSelectable ? (
                                  <button
                                    onClick={() => handleSelectWorkers(application)}
                                    className="flex items-center space-x-1 bg-primary text-white px-3 py-1.5 rounded-lg hover:bg-primary-700 transition-colors text-sm"
                                  >
                                    <Icon name="Users" size={14} />
                                    <span>Select workers</span>
                                  </button>
                                ) : (
                                  <span className="text-xs text-text-tertiary italic">Selection locked</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmployerApplicationManagement;
